import React, { useEffect, useState } from 'react';
import api from '../../lib/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, Columns2, Check } from 'lucide-react';
import { toast } from 'sonner';

const TONES = [
  { value: 'freundlich', label: 'Freundlich' },
  { value: 'formell', label: 'Formell' },
  { value: 'herzlich', label: 'Herzlich' },
  { value: 'sachlich', label: 'Sachlich' },
  { value: 'bestimmt', label: 'Bestimmt' },
];

const toneLabel = (v) => TONES.find((t) => t.value === v)?.label || v;

function VariantCard({ variant, side, onAdopt }) {
  if (!variant) return null;
  return (
    <div className="flex-1 min-w-0 flex flex-col rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`tone-variant-${side}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
        <Badge variant="secondary" className="text-xs">Ton: {toneLabel(variant.tone)}</Badge>
        <Button size="sm" className="h-7 text-xs" onClick={() => onAdopt(variant)} data-testid={`tone-adopt-${side}`}>
          <Check className="h-3.5 w-3.5 mr-1" /> Übernehmen
        </Button>
      </div>
      <div className="px-3 py-2 border-b border-slate-100">
        <div className="text-[11px] text-slate-400">Betreff</div>
        <div className="text-sm font-medium text-slate-800">{variant.subject || '–'}</div>
      </div>
      <div className="px-3 py-2 overflow-y-auto text-xs text-slate-700 whitespace-pre-wrap font-mono flex-1" style={{ maxHeight: '45vh' }}>
        {variant.body}
      </div>
    </div>
  );
}

export function ToneCompareDialog({ open, onClose, source, defaultTone, onAdopt }) {
  const [toneA, setToneA] = useState('freundlich');
  const [toneB, setToneB] = useState('formell');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      const a = defaultTone || 'freundlich';
      setToneA(a);
      setToneB(a === 'formell' ? 'freundlich' : 'formell');
      setResult(null);
    }
  }, [open, defaultTone]);

  const hasBody = !!source?.body?.trim();
  const canRun = hasBody || !!source?.prompt?.trim() || !!source?.occasion;

  const run = async () => {
    if (toneA === toneB) { toast.error('Bitte zwei unterschiedliche Tonalitäten wählen'); return; }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post('/correspondence/ai/compare', {
        subject: source?.subject || '', body: source?.body || '',
        prompt: source?.prompt || '', occasion: source?.occasion || '',
        tone_a: toneA, tone_b: toneB,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ton-Vorschau fehlgeschlagen');
    } finally { setLoading(false); }
  };

  const adopt = (variant) => {
    onAdopt(variant);
    onClose();
    toast.success(`Variante „${toneLabel(variant.tone)}" übernommen – bitte prüfen`);
  };

  const selectCls = 'h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-brand-red/40';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl" data-testid="tone-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Columns2 className="h-5 w-5 text-brand-red" /> Ton-Vorschau
          </DialogTitle>
          <DialogDescription>
            {hasBody
              ? 'Der aktuelle Text wird von der KI in zwei Tonalitäten umformuliert – wähle die bessere Variante.'
              : 'Die KI erstellt den Text (aus Anlass/Beschreibung) in zwei Tonalitäten – wähle die bessere Variante.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-slate-500">Variante A:</span>
            <select value={toneA} onChange={(e) => setToneA(e.target.value)} className={selectCls} data-testid="tone-select-a">
              {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <span className="text-xs font-medium text-slate-500 ml-2">Variante B:</span>
            <select value={toneB} onChange={(e) => setToneB(e.target.value)} className={selectCls} data-testid="tone-select-b">
              {TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <div className="flex-1" />
            <Button size="sm" onClick={run} disabled={loading || !canRun} data-testid="tone-compare-run">
              {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Columns2 className="h-4 w-4 mr-1.5" />}
              {result ? 'Neu erstellen' : 'Varianten erstellen'}
            </Button>
          </div>
          {!canRun && (
            <p className="text-xs text-brand-red">Bitte zuerst Text eingeben, einen Anlass wählen oder eine Beschreibung in der KI-Texterstellung angeben.</p>
          )}
          {loading && (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin text-brand-red" />
              Die KI erstellt beide Varianten parallel…
            </div>
          )}
          {result && !loading && (
            <div className="flex gap-3" data-testid="tone-compare-results">
              <VariantCard variant={result.variant_a} side="a" onAdopt={adopt} />
              <VariantCard variant={result.variant_b} side="b" onAdopt={adopt} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
